import config from '../../config';
import API from '../api';

const baseUrl = config.coreApi.baseUrl;

//#region TYPES
/**
 * @typedef User
 * @type {Object}
 * @property {string} email - Email of the user.
 * @property {string} displayName - Display name of the user.
 * @property {string} id - ID of the user.
 * @property {object} preferences - Preferences of the user.
 */
//#endregion

//#region CREATE

/**
 * @description Gets a batch of user info by email.
 * @param {string} filter - Filter expression.
 * @param {string[]} emails - List of user emails.
 * @param {string} orderBy - Order expression.
 * @param {number} pageSize - Max amount of users returned.
 * @param {string} pageToken - Page token (first page is defined as empty string).
 * @returns {{users: [{ displayName: string, email: string, id: string}], nextPageToken: string, totalSize: number}} List of users.
 */
const getUsersBatchByEmail = async (emails = [], filter = '', orderBy = 'displayName, id', pageSize = 40, pageToken = '') => {
  const sessionVersion = API.sessionVersion;
  await API.refreshTokenWhenNeeded();
  const token = API.isImpersonating ? API.originalToken : API.access_token;

  const url = `${baseUrl}/users:batchGetByEmail`;
  const queryObject = {
    filter,
    emails,
    orderBy,
    pageSize,
    pageToken
  };

  const users = await fetch(url, {
    headers: {
      ...API.headers,
      'X-Request-Id': crypto.randomUUID(),
      Authorization: `Bearer ${token}`
    },
    method: 'POST',
    body: JSON.stringify(queryObject)
  }).then(res => API.generateResponse(res));

  if (API.sessionVersion !== sessionVersion) throw new Error('Session changed during authentication.');
  return users;
};

const getUsersBatchById = async (ids = [], filter = '', orderBy = 'displayName, id', pageSize = 40, pageToken = '') => {
  await API.refreshTokenWhenNeeded();

  const url = `${baseUrl}/users:batchGet`;
  const names = ids.map(id => `users/${id}`);

  const queryObject = {
    filter,
    names,
    orderBy,
    pageSize,
    pageToken
  };

  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'POST',
    body: JSON.stringify(queryObject)
  }).then(res => API.generateResponse(res));
};

//#endregion
//#region READ
/**
 * @description Gets the current user information.
 * @param {string} accessToken - Access token.
 * @returns {User} Current user.
 */
const getCurrentUser = async accessToken => {
  if (!accessToken) await API.refreshTokenWhenNeeded();

  const url = `${baseUrl}/currentuser`;
  const token = accessToken || (API.isImpersonating ? API.originalToken : API.access_token);
  return fetch(url, {
    headers: {
      ...API.headers,
      'X-Request-Id': crypto.randomUUID(),
      Authorization: `Bearer ${token}`
    },
    method: 'GET'
  }).then(API.generateResponse);
};

/**
 * @description Gets the custom widgets for the current user.
 * @param {string} accessToken - Access token.
 * @returns {{customWidgets: object}} custom widgets.
 */
const getUserCustomWidgets = async accessToken => {
  if (!accessToken) await API.refreshTokenWhenNeeded();
  const token = accessToken || (API.isImpersonating ? API.originalToken : API.access_token);

  const url = `${baseUrl}/currentuser:getCustomWidgets`;
  return fetch(url, {
    headers: {
      ...API.headers,
      'X-Request-Id': crypto.randomUUID(),
      Authorization: `Bearer ${token}`
    },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

/**
 * @description Check is a feature flag is active for the current user.
 * @param {string} featureFlag - Feature flag to check.
 * @returns {{ active: boolean }} active feature flags.
 */
const getActiveFeatureFlag = async featureFlag => {
  await API.refreshTokenWhenNeeded();

  const url = `${baseUrl}/currentuser:isFeatureFlagActive?featureFlag=${featureFlag}`;

  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};
//#endregion

//#region UPDATE

/**
 * @description Updates the current user.
 * @param {string} accessToken - Access token for authentication.
 * @param {User} user - User object containing updated fields.
 * @param {string[]} fields - List of user fields to update.
 * @returns {User} The updated user information.
 */
const updateCurrentUser = async (user, fields) => {
  await API.refreshTokenWhenNeeded();

  const paths = `updateMask=${fields.join(',')}`;
  const body = {};
  const url = `${baseUrl}/currentuser?${paths}`;

  fields.forEach(field => {
    body[field] = user[field];
  });

  return fetch(url, {
    method: 'PATCH',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify(body)
  }).then(res => API.generateResponse(res));
};

/**
 * @description Updates the custom widgets for the current user.
 * @param {string} accessToken - Access token for authentication.
 * @param {object} customWidgets - Object containing custom widgets for the user.
 * @returns {object} The updated custom widgets for the user.
 */
const updateCustomWidgets = async (accessToken, customWidgets) => {
  await API.refreshTokenWhenNeeded();
  const token = API.isImpersonating ? API.originalToken : API.access_token || accessToken;

  const url = `${baseUrl}/currentuser:updateCustomWidgets`;

  return fetch(url, {
    method: 'PATCH',
    headers: {
      ...API.headers,
      'X-Request-Id': crypto.randomUUID(),
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(customWidgets)
  }).then(res => API.generateResponse(res));
};
//#endregion

const impersonateUser = async (userId, token) => {
  if (!token) {
    await API.refreshTokenWhenNeeded();
  }

  const url = `${baseUrl}/currentuser:impersonate`;
  const impersonatorToken = token || (API.isImpersonating ? API.originalToken : API.access_token);
  const sessionVersion = API.sessionVersion;

  const user = await fetch(url, {
    method: 'POST',
    headers: {
      ...API.headers,
      'X-Request-Id': crypto.randomUUID(),
      Authorization: `Bearer ${impersonatorToken}`
    },
    body: JSON.stringify({
      targetName: `users/${userId}`
    })
  }).then(API.generateResponse);

  if (API.sessionVersion !== sessionVersion) throw new Error('Session changed during authentication.');
  API.isImpersonating = true;
  API.userId = userId;
  API.originalToken = impersonatorToken;
  return user;
};

export {
  getUsersBatchById,
  getUsersBatchByEmail,
  getCurrentUser,
  getUserCustomWidgets,
  getActiveFeatureFlag,
  updateCurrentUser,
  updateCustomWidgets,
  impersonateUser
};
